import { Dialog, DialogProps } from '@sparemin/blockhead';
import React, { useCallback, useState } from 'react';
import { ALL_FEATURES, AuthProvider } from 'ui/context';
import { AuthProviderProps } from 'ui/context/AuthContext/types';
import { AuthenticationProvider, AuthStep, UserAuthResult } from 'ui/types';
import ForgotPasswordCard from '../ForgotPasswordCard';
import LoginCard from '../LoginCard/LoginCard';
import SignUpCard from '../SignUpCard/SignUpCard';

export type ReopenStrategy = 'continue' | 'reset';

export interface UserAuthenticationModalProps
  extends Pick<DialogProps, 'isOpen' | 'onClose'>,
    Omit<AuthProviderProps, 'children' | 'navigation'> {
  /**
   * All of the features available in the auth modal. It might be useful, for example,
   * to remove the "forgot-password" feature as all apps do not yet support that flow
   */
  features?: AuthStep[];
  /**
   * The step that the modal should start on when it opens.  Defaults to `"login"`.
   */
  initialStep?: AuthStep;
  /**
   * Called when a step changes.
   *
   * `fromStep` - the source step
   * `toStep` - the destination step
   */
  onStepChange?: (fromStep: AuthStep, toStep: AuthStep) => void;
  /**
   * Called when the user beings an authentication request by clicking on a form
   * submit button or a 3rd party auth button.
   */
  onSubmit?: (step: AuthStep, provider: AuthenticationProvider) => void;
  /**
   * Called when the authentication request is successful
   */
  onSuccess?: (
    step: AuthStep,
    provider?: AuthenticationProvider,
    data?: UserAuthResult,
  ) => void;
  onAfterClose?: () => void;
  /**
   * Determines what happens when the modal reopens.
   *
   * If set to `continue`, then the modal reopen at the same step that was displayed
   * when it was closed.
   *
   * If set to `reset`, the modal will render the `initialStep` when it is reopened.
   */
  reopenStrategy?: ReopenStrategy;
}

const ARIA_LABELS: Record<AuthStep, string> = {
  login: 'login dialog',
  'sign-up': 'user registration dialog',
  'forgot-password': 'forgot password dialog',
  'reset-password': 'reset password dialog',
};

/**
 * An authentication UI that is rendered in a modal so that a user can login or
 * register without leaving their current page.
 */
export const UserAuthenticationModal: React.FC<
  UserAuthenticationModalProps
> = ({
  features = ALL_FEATURES,
  isOpen,
  initialStep = 'login',
  onClose,
  onSubmit,
  onStepChange,
  onSuccess,
  promotionKey,
  referralKey,
  reopenStrategy = 'continue',
  traceId,
  onAfterClose,
}) => {
  const [activeStep, setActiveStep] = useState<AuthStep>(initialStep);
  const [prevIsOpen, setPrevIsOpen] = useState(isOpen);

  // set modal to the initial step when it opens
  if (isOpen !== prevIsOpen) {
    setPrevIsOpen(isOpen);

    if (isOpen && initialStep !== activeStep && reopenStrategy === 'reset') {
      setActiveStep(initialStep);
    }
  }

  const handleStepChange = useCallback(
    (nextStep: AuthStep) => {
      onStepChange?.(activeStep, nextStep);
      setActiveStep(nextStep);
    },
    [activeStep, onStepChange],
  );

  // onSubmit is called with the step name and the provider to which the auth
  // request is sent.  most of the components below have an onSubmit callback
  // that fires with the provider.  the Headliner auth mechanism (form) doesn't
  // call onSubmit with a provider, so the override parameter can be used
  // so that "headliner" gets passed to onSubmit
  const createSubmitHandler =
    (step: AuthStep, providerOverride?: AuthenticationProvider) =>
    (provider?: AuthenticationProvider) => {
      const resolvedProvider = providerOverride ?? provider;

      if (!resolvedProvider) {
        throw new Error('Submit handler has no provider');
      }

      onSubmit?.(step, resolvedProvider);
    };

  const createSuccessHandler =
    (step: AuthStep) =>
    async (provider?: AuthenticationProvider, data?: UserAuthResult) => {
      return onSuccess?.(step, provider, data);
    };

  return (
    <AuthProvider
      navigation={{
        onStepChange: handleStepChange,
        onClose: () => onClose?.(),
      }}
      {...{ promotionKey, referralKey, traceId, features }}
    >
      <Dialog
        aria-label={ARIA_LABELS[activeStep]}
        onExited={() => {
          setActiveStep(initialStep);
          onAfterClose?.();
        }}
        position="padded"
        {...{ isOpen, onClose }}
      >
        {activeStep === 'login' && features.includes('login') && (
          <LoginCard
            onAuthenticateSuccess={createSuccessHandler('login')}
            onSubmit={createSubmitHandler('login')}
          />
        )}
        {activeStep === 'sign-up' && features.includes('sign-up') && (
          <SignUpCard
            onAuthenticateSuccess={createSuccessHandler('sign-up')}
            onSubmit={createSubmitHandler('sign-up')}
          />
        )}
        {activeStep === 'forgot-password' &&
          features.includes('forgot-password') && (
            <ForgotPasswordCard
              onSubmit={createSubmitHandler('forgot-password', 'headliner')}
              onSuccess={createSuccessHandler('forgot-password')}
            />
          )}
      </Dialog>
    </AuthProvider>
  );
};

export default UserAuthenticationModal;
